/* eslint-disable no-console */

import _ from 'lodash';
import { ServiceContainer } from '@tv2-player/service-container/src/serviceContainer';
import { PlatformMetadataService } from '@tv2-player/service-types/src/PlatformMetadataService';
import stringify from 'fast-safe-stringify';
import { OverseerMessageHandler, OverseerMessageHandlerClass } from './overseerMessageHandler';
import { devFlags } from '../devFlags';

type OverseerUIEvent = {
  session: string;
  data: {
    timestamp: number;
    plugin: string;
    name: string;
    value: any;
    metadata: any;
  };
};

/**
 * An implementation of OverseerMessageHandler that sends the data to the overseer UI
 */
export class SendToOverseerUIMessageHandler extends OverseerMessageHandlerClass {
  #isLocalhost = window.location.hostname.startsWith('localhost');
  #events: OverseerUIEvent[] = [];
  #runningCall: Promise<any> | null = null;
  #keepalive: boolean = true;
  #baseApiUrl: string;
  #killOnFailures;

  constructor(
    keepalive: undefined | false | true,
    killOnFailures: undefined | false | true,
    baseApiUrl: string
  ) {
    super();
    if (ServiceContainer.config.get(devFlags.enableOverseer)) {
      this.enable();
    }
    this.#baseApiUrl = baseApiUrl;
    if (keepalive !== undefined) this.#keepalive = keepalive;

    document.onvisibilitychange = () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    };

    this.#killOnFailures = killOnFailures;
  }

  get logEndpoint() {
    return this.#baseApiUrl;
  }

  trackPluginCall(plugin: string, name: string, value?: any, metadata?: any): void {
    if (!this.enabled) return;
    if (!this.hasSession) return;

    this.#events.push({
      session: this.sessionId,
      data: { timestamp: new Date().getTime(), plugin, name, value, metadata },
    });

    this.#flushWithDebounce();
  }

  #handleCatch(e: any, events: any[]) {
    console.error(e);
    this.#events.push(...events);
    if (this.#killOnFailures) {
      this.disable();
    }
  }

  #flushWithDebounce = _.debounce(() => this.flush(), 500, { maxWait: 1500 });

  flush() {
    if (!this.#events) return false;

    // We will wait for the previous call to finish before we start a new one
    return Promise.resolve(this.#runningCall).then(() => {
      // For each session, we will send a POST request to the overseer UI
      const events = this.#events;
      this.#events = [];
      const promises = Object.entries(_.groupBy(events, x => x.session)).map(
        async ([sessionId, value]) => {
          const body = { data: value.map(e => e.data) };

          try {
            const response = await fetch(`${this.logEndpoint}/api/v1/sessions/${sessionId}/posts`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: stringify(body),
              keepalive: this.#keepalive,
            });

            if (!response.ok) {
              this.#handleCatch(response.statusText, value);
            }
          } catch (e) {
            return this.#handleCatch(e, value);
          }
        }
      );

      this.#runningCall = Promise.all(promises);
      return Promise.all(promises);
    });
  }

  createSession(session: string): void {
    if (this.hasSession) return;

    this.sessionId = session;
    this.hasSession = true;

    const metadata = {
      userAgent: navigator.userAgent ?? '',
      ...(ServiceContainer.get(PlatformMetadataService) ?? {}),
    };

    this.setMetadata(metadata);
  }

  setMetadata(metadata: Record<string, any>): void {
    fetch(`${this.logEndpoint}/api/v1/sessions/${this.sessionId}/metadata`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: metadata,
      }),
    }).catch(e => {
      return this.#handleCatch(e, []);
    });
  }

  static register({
    keepalive,
    killOnFailures,
    baseApiUrl,
  }: {
    keepalive?: boolean;
    killOnFailures?: boolean;
    baseApiUrl: string;
  }) {
    ServiceContainer.register(OverseerMessageHandler).instance(
      new SendToOverseerUIMessageHandler(keepalive, killOnFailures, baseApiUrl)
    );
  }
}
